// The branch workspace: every unsaved change of one branch as drafts, a change log coalesced per field, undo / redo,
// and the bookkeeping Save All needs (spec/dashboard.md section 4). Pure TypeScript; React binds to it through
// `useWorkspace` (./react.tsx). Everything here is serialisable so a reload restores it (section 4.6).
import type { ErrorDetail, Json } from '../api/types';
import { clone, deepEqual, getAt, setAt } from './paths';

export type EntityKind = 'document' | 'string' | 'product' | 'template-selected' | 'player-key' | 'asset' | 'localization-settings' | 'environment' | 'platform-config';
export type EntityKey = `${EntityKind}:${string}`;
export type DraftStatus = 'new' | 'modified' | 'deleted';
export type Actor = 'user' | `extension:${string}`;

export interface FieldChange {
  path: string;
  before: unknown;
  after: unknown;
  at: number;
}

export interface SaveError {
  source: 'server' | 'extension' | 'network';
  message: string;
  details?: ErrorDetail[] | null;
  /** the extensions' reasons, one per blocking extension */
  reasons?: { extension: string; reason: string }[];
}

export interface Conflict {
  remoteVersion: number | null;
  remoteValue: Json | undefined;
  by?: string;
  at?: string;
  message: string;
  /** the player is online (section 11.4, the online guard): the only override is "Save anyway" (force) */
  online?: boolean;
}

export interface Draft {
  key: EntityKey;
  kind: EntityKind;
  /** the server id (or the temp id of a new entity) */
  id: string;
  status: DraftStatus;
  base: { version: number | null; value: Json } | null;
  current: Json;
  changes: FieldChange[];
  actor: Actor;
  label: string;
  template?: string;
  error?: SaveError;
  conflict?: Conflict;
  /** the extensions' warnings of the last save attempt (section 10.6; a warning never blocks) */
  warnings?: string[];
  /** a player key the user chose to save over an online player (section 11.4): the next save sends `force: true` */
  force?: boolean;
  /** "changed by X at …" from the head poll, before a save was tried */
  remote?: { by?: string; at?: string; sha: string };
  createdAt: number;
}

export interface DraftMeta {
  label?: string;
  template?: string;
  actor?: Actor;
}

type Command =
  | {
      type: 'edit';
      key: EntityKey;
      path: string;
      before: unknown;
      after: unknown;
      actor: Actor;
      /** kept when the undo emptied the draft, so a redo can rebuild it from the base */
      base?: { version: number | null; value: Json } | null;
      label?: string;
      template?: string;
    }
  | { type: 'create'; key: EntityKey; snapshot: Draft }
  | { type: 'remove'; key: EntityKey; previous: Draft | null; snapshot: Draft }
  | { type: 'restore'; key: EntityKey; previous: Draft; after: Draft | null };

export interface SaveSummary {
  at: number;
  saved: number;
  failed: number;
  blocked: number;
  dryRun: boolean;
  commit?: string | null;
  /** the new documents' temp ids → the ids the server assigned (postSave's idMapping, section 10.6) */
  idMapping?: Record<string, string>;
  /** what the server reported besides the results: the `unnySystem` bumps of player edits (section 11.4) */
  notes?: string[];
}

export interface WorkspaceSnapshot {
  version: 1;
  gameId: string;
  branch: string;
  drafts: Draft[];
  undo: Command[];
  redo: Command[];
  tempCounter: number;
  head: string | null;
}

export function entityKey(kind: EntityKind, id: string): EntityKey {
  return `${kind}:${id}`;
}

export function isTempId(id: string): boolean {
  return id.startsWith('$');
}

function splitKey(key: EntityKey): [EntityKind, string] {
  const i = key.indexOf(':');
  return [key.slice(0, i) as EntityKind, key.slice(i + 1)];
}

function isPlainObject(x: unknown): x is Record<string, Json> {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
}

export class Workspace {
  private drafts = new Map<EntityKey, Draft>();
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];
  private listeners = new Set<() => void>();
  private tempCounter = 0;
  /** the last known branch head (X-Huelency-Commit / branch.head) */
  head: string | null = null;
  /** the heads this workspace's own writes produced (section 4.6) */
  private producedHeads = new Set<string>();
  /** the paths changed remotely since the last Refresh (section 4.6) */
  private remotePaths = new Set<string>();
  isProtected = false;
  lastSave: SaveSummary | null = null;
  /** when the drafts were restored from storage, how many (the banner) */
  restoredCount: number | null = null;
  /**
   * The commit message the user typed in the Changes panel for the next Save All (section 4.3); transient: not part of the
   * snapshot, cleared after a save that left nothing behind. Empty means the automatic summary (./summary.ts).
   */
  saveMessage = '';
  private version = 0;

  constructor(
    public readonly gameId: string,
    public readonly branch: string,
  ) {}

  // ---- subscription (useSyncExternalStore)

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** A counter that moves on every change; React re-renders on it. */
  getVersion(): number {
    return this.version;
  }

  private emit(): void {
    this.version++;
    for (const l of Array.from(this.listeners)) l();
  }

  // ---- reads

  get all(): Draft[] {
    return Array.from(this.drafts.values());
  }

  getDraft(key: EntityKey): Draft | undefined {
    return this.drafts.get(key);
  }

  /** Number of entries in the change log: field changes, plus one per deleted entity and per new entity without fields. */
  get changeCount(): number {
    let n = 0;
    for (const d of this.drafts.values()) {
      if (d.status === 'deleted') n += 1;
      else n += Math.max(d.status === 'new' ? 1 : 0, d.changes.length);
    }
    return n;
  }

  get isDirty(): boolean {
    return this.drafts.size > 0;
  }

  /** The entity as the user sees it: the draft's value over the server's; undefined when deleted or unknown. */
  overlay<T extends Json>(key: EntityKey, server: T | undefined): T | undefined {
    const d = this.drafts.get(key);
    if (!d) return server;
    if (d.status === 'deleted') return undefined;
    return d.current as T;
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** What Undo would revert, for the top bar's button (section 4.2): "name of Summer Deal", "delete of Weekend"; null when nothing. */
  undoLabel(): string | null {
    const cmd = this.undoStack[this.undoStack.length - 1];
    return cmd ? this.describe(cmd) : null;
  }

  /** What Redo would re-apply; null when nothing. */
  redoLabel(): string | null {
    const cmd = this.redoStack[this.redoStack.length - 1];
    return cmd ? this.describe(cmd) : null;
  }

  setSaveMessage(message: string): void {
    if (message === this.saveMessage) return;
    this.saveMessage = message;
    this.emit();
  }

  // ---- edits

  /** Starts (or returns) the draft of a server entity; the base is what the server had when the draft began. */
  ensure(kind: EntityKind, id: string, base: { version: number | null; value: Json }, meta: DraftMeta = {}): Draft {
    const key = entityKey(kind, id);
    let d = this.drafts.get(key);
    if (!d) {
      d = {
        key,
        kind,
        id,
        status: 'modified',
        base: { version: base.version, value: clone(base.value) },
        current: clone(base.value),
        changes: [],
        actor: meta.actor ?? 'user',
        label: meta.label ?? id,
        template: meta.template,
        createdAt: Date.now(),
      };
      this.drafts.set(key, d);
    } else {
      if (meta.label) d.label = meta.label;
      if (meta.template) d.template = meta.template;
    }
    return d;
  }

  /**
   * Sets one field of an entity. For a server entity `base` is required the first time (the current server value and
   * version); later edits find the draft. Returns false when the value did not change.
   */
  edit(kind: EntityKind, id: string, path: string, value: unknown, opts: { base?: { version: number | null; value: Json } } & DraftMeta = {}): boolean {
    const key = entityKey(kind, id);
    let d = this.drafts.get(key);
    if (!d) {
      if (!opts.base) throw new Error(`no draft for ${key} and no base given`);
      d = this.ensure(kind, id, opts.base, opts);
    }
    if (d.status === 'deleted') return false;
    const before = getAt(d.current, path);
    if (deepEqual(before, value)) return false;
    const actor = opts.actor ?? d.actor;
    this.apply(d, path, value);
    this.push({ type: 'edit', key, path, before: clone(before), after: clone(value), actor });
    this.emit();
    return true;
  }

  /** Stages a new entity; returns its temp id (`$d1`, `$d2`, …), which references may use until Save All maps it. */
  create(kind: EntityKind, data: Json, meta: DraftMeta & { tempId?: string; defaults?: Json } = {}): string {
    const id = meta.tempId ?? `$d${++this.tempCounter}`;
    const key = entityKey(kind, id);
    const now = Date.now();
    // one change per field that differs from the defaults (a document's template defaults; section 4.1): a field inside a
    // top-level object (`data.<param>`) is one field; without defaults every top-level key is a change
    const changes: FieldChange[] = [];
    const defaults: Record<string, Json> = isPlainObject(meta.defaults) ? meta.defaults : {};
    const maybe = (x: unknown) => (x === undefined ? undefined : clone(x as Json));
    if (isPlainObject(data)) {
      for (const [k, v] of Object.entries(data)) {
        const dv = defaults[k];
        if (isPlainObject(v) && isPlainObject(dv)) {
          for (const ck of new Set([...Object.keys(v), ...Object.keys(dv)])) {
            if (!deepEqual(v[ck], dv[ck])) changes.push({ path: `${k}.${ck}`, before: maybe(dv[ck]), after: maybe(v[ck]), at: now });
          }
        } else if (!deepEqual(v, dv)) changes.push({ path: k, before: maybe(dv), after: maybe(v), at: now });
      }
    }
    const d: Draft = {
      key,
      kind,
      id,
      status: 'new',
      base: null,
      current: clone(data),
      changes,
      actor: meta.actor ?? 'user',
      label: meta.label ?? id,
      template: meta.template,
      createdAt: now,
    };
    // a new entity's value carries its temp id, so the grid, the pickers and the modal address it before the save maps it
    if (isPlainObject(d.current) && 'id' in d.current) (d.current as Record<string, Json>).id = id;
    this.drafts.set(key, d);
    this.push({ type: 'create', key, snapshot: clone(d) });
    this.emit();
    return id;
  }

  /** Marks a server entity deleted (a new entity is simply dropped). */
  remove(kind: EntityKind, id: string, base?: { version: number | null; value: Json }, meta: DraftMeta = {}): void {
    const key = entityKey(kind, id);
    const existing = this.drafts.get(key);
    if (existing?.status === 'new') {
      this.drafts.delete(key);
      this.push({ type: 'remove', key, previous: clone(existing), snapshot: clone(existing) });
      this.emit();
      return;
    }
    if (existing?.status === 'deleted') return;
    const previous = existing ? clone(existing) : null;
    const d = existing ?? this.ensure(kind, id, base ?? { version: null, value: null }, meta);
    d.status = 'deleted';
    this.push({ type: 'remove', key, previous, snapshot: clone(d) });
    this.emit();
  }

  /** The inverse of `remove` for a deleted server entity. */
  restore(kind: EntityKey | EntityKind, id?: string): void {
    const key = id === undefined ? (kind as EntityKey) : entityKey(kind as EntityKind, id);
    const d = this.drafts.get(key);
    if (!d || d.status !== 'deleted') return;
    const previous = clone(d);
    let after: Draft | null;
    if (d.changes.length === 0) {
      this.drafts.delete(key); // nothing else was staged: the entity is back to the server's state
      after = null;
    } else {
      d.status = 'modified';
      after = clone(d);
    }
    this.push({ type: 'restore', key, previous, after }); // undo puts the deletion back, redo restores again
    this.emit();
  }

  // ---- discards (not undoable: they are themselves the undo of staged work; Discard All asks for confirmation)

  discardChange(key: EntityKey, path: string): void {
    const d = this.drafts.get(key);
    if (!d) return;
    const i = d.changes.findIndex((c) => c.path === path);
    if (i < 0) return;
    const change = d.changes[i]!;
    d.changes.splice(i, 1);
    if (d.status === 'new') {
      d.current = setAt(d.current, path, undefined);
      if (d.changes.length === 0) this.drafts.delete(key);
    } else {
      d.current = setAt(d.current, path, change.before);
      if (d.changes.length === 0 && d.status === 'modified') this.drafts.delete(key);
    }
    this.forgetCommands(key);
    this.emit();
  }

  discardEntity(key: EntityKey): void {
    if (!this.drafts.delete(key)) return;
    this.forgetCommands(key);
    this.emit();
  }

  discardAll(): void {
    this.drafts.clear();
    this.undoStack = [];
    this.redoStack = [];
    this.emit();
  }

  /**
   * Moves every draft, with the undo / redo history, into another branch's workspace (section 4.3 step 6: the way out of a
   * protected branch is a new branch forked from its head, whose base versions are the same, so the drafts save there as
   * they would have here). The target must be clean: this refuses rather than merge. The target adopts this head when it
   * has none and a temp-id counter past this one's; this workspace is cleared (Discard All without the confirmation).
   * Returns the number of drafts moved.
   */
  moveDraftsTo(target: Workspace): number {
    if (target === this) throw new Error('A workspace cannot move its drafts to itself.');
    if (target.isDirty) throw new Error(`Branch ${target.branch} already holds unsaved changes.`);
    const moved = this.drafts.size;
    for (const d of this.drafts.values()) target.drafts.set(d.key, clone(d));
    target.undoStack = clone(this.undoStack);
    target.redoStack = clone(this.redoStack);
    target.tempCounter = Math.max(target.tempCounter, this.tempCounter);
    if (target.head === null && this.head !== null) target.head = this.head;
    if (!target.saveMessage) target.saveMessage = this.saveMessage;
    this.drafts.clear();
    this.undoStack = [];
    this.redoStack = [];
    this.saveMessage = '';
    target.emit();
    this.emit();
    return moved;
  }

  // ---- undo / redo

  undo(): boolean {
    const cmd = this.undoStack.pop();
    if (!cmd) return false;
    this.invert(cmd, 'undo');
    this.redoStack.push(cmd);
    this.emit();
    return true;
  }

  redo(): boolean {
    const cmd = this.redoStack.pop();
    if (!cmd) return false;
    this.invert(cmd, 'redo');
    this.undoStack.push(cmd);
    this.emit();
    return true;
  }

  /** The undo stack's position, for a modal's Cancel (section 4.4). */
  mark(): number {
    return this.undoStack.length;
  }

  /** Undoes back to a mark; the undone commands stay redoable. */
  revertTo(mark: number): void {
    while (this.undoStack.length > mark) {
      const cmd = this.undoStack.pop()!;
      this.invert(cmd, 'undo');
      this.redoStack.push(cmd);
    }
    this.emit();
  }

  // ---- save bookkeeping (./save.ts drives it)

  /** A draft was applied: it leaves the workspace; a temp id is rewritten everywhere it was referenced. */
  markSaved(key: EntityKey, result: { id?: string | null; version?: number | null }): void {
    const d = this.drafts.get(key);
    if (!d) return;
    this.drafts.delete(key);
    this.forgetCommands(key);
    if (isTempId(d.id) && result.id) this.rewriteTempId(d.id, result.id);
  }

  markFailed(key: EntityKey, error: SaveError): void {
    const d = this.drafts.get(key);
    if (!d) return;
    d.error = error;
    delete d.conflict;
  }

  /** The extensions' warnings for an entity (shown with the save result; the entity still saves). */
  markWarnings(key: EntityKey, warnings: string[]): void {
    const d = this.drafts.get(key);
    if (!d) return;
    d.warnings = [...(d.warnings ?? []), ...warnings];
  }

  markConflict(key: EntityKey, conflict: Conflict): void {
    const d = this.drafts.get(key);
    if (!d) return;
    d.conflict = conflict;
    delete d.error;
  }

  clearOutcomes(): void {
    for (const d of this.drafts.values()) {
      delete d.error;
      delete d.conflict;
      delete d.warnings;
    }
  }

  /** Conflict choice "overwrite": the draft adopts the remote version so the next save's ifVersion matches. */
  adoptRemoteVersion(key: EntityKey): void {
    const d = this.drafts.get(key);
    if (!d?.conflict) return;
    if (d.base) d.base.version = d.conflict.remoteVersion;
    delete d.conflict;
    delete d.remote;
    this.emit();
  }

  /** The online guard's choice "Save anyway" (section 11.4): the draft is sent with `force: true` next time. */
  setForce(key: EntityKey, force = true): void {
    const d = this.drafts.get(key);
    if (!d) return;
    d.force = force;
    if (force) delete d.conflict;
    this.emit();
  }

  /** Conflict choice "reload and re-apply": the coalesced changes are replayed on the remote value. */
  rebaseOnRemote(key: EntityKey): void {
    const d = this.drafts.get(key);
    if (!d?.conflict) return;
    const remote = d.conflict.remoteValue ?? null;
    let current = clone(remote);
    const kept: FieldChange[] = [];
    for (const c of d.changes) {
      const remoteBefore = getAt(remote, c.path);
      if (deepEqual(remoteBefore, c.after)) continue; // the other side made the same change
      current = setAt(current, c.path, clone(c.after));
      kept.push({ ...c, before: clone(remoteBefore) });
    }
    d.base = { version: d.conflict.remoteVersion, value: clone(remote) };
    d.current = current;
    d.changes = kept;
    delete d.conflict;
    delete d.remote;
    if (kept.length === 0 && d.status === 'modified') this.drafts.delete(key);
    this.emit();
  }

  /** The head poll found these entities changed by someone else since the drafts started (section 4.6). */
  flagRemote(keys: EntityKey[], info: { by?: string; at?: string; sha: string }): void {
    let touched = false;
    for (const key of keys) {
      const d = this.drafts.get(key);
      if (!d) continue;
      d.remote = info;
      touched = true;
    }
    if (touched) this.emit();
  }

  /**
   * The branch head moved. A write of this workspace's own (`X-Huelency-Commit`, the default) is remembered as a head it
   * produced, so the stream's `branch.head` echo of it is not counted as a remote change (section 4.6); the head watcher
   * passes `{ remote: true }`.
   */
  setHead(sha: string | null, opts: { remote?: boolean } = {}): void {
    if (sha && !opts.remote) {
      this.producedHeads.add(sha);
      if (this.producedHeads.size > 200) this.producedHeads.delete(this.producedHeads.values().next().value as string);
    }
    if (sha === this.head) return;
    this.head = sha;
    this.emit();
  }

  /** True when the commit is one this workspace produced (its own write). */
  produced(sha: string | null | undefined): boolean {
    return !!sha && this.producedHeads.has(sha);
  }

  /** Paths someone else changed on the branch since the page last refreshed (the "N changes landed" banner). */
  noteRemoteChanges(paths: readonly string[]): void {
    const before = this.remotePaths.size;
    for (const p of paths) if (p) this.remotePaths.add(p);
    if (this.remotePaths.size !== before) this.emit();
  }

  get remoteChangeCount(): number {
    return this.remotePaths.size;
  }

  clearRemoteChanges(): void {
    if (this.remotePaths.size === 0) return;
    this.remotePaths.clear();
    this.emit();
  }

  setProtected(value: boolean): void {
    if (value === this.isProtected) return;
    this.isProtected = value;
    this.emit();
  }

  setLastSave(summary: SaveSummary | null): void {
    this.lastSave = summary;
    this.emit();
  }

  /** The command stack is cleared for what a successful save removed; what failed keeps its history. */
  afterSave(): void {
    const remaining = new Set(this.drafts.keys());
    this.undoStack = this.undoStack.filter((c) => remaining.has(c.key));
    this.redoStack = this.redoStack.filter((c) => remaining.has(c.key));
    this.emit();
  }

  /** Every draft with this actor, in creation order (the panel's groups and the per-actor batches). */
  draftsOf(actor: Actor): Draft[] {
    return this.all.filter((d) => d.actor === actor).sort((a, b) => a.createdAt - b.createdAt);
  }

  get actors(): Actor[] {
    const set = new Set<Actor>();
    for (const d of this.drafts.values()) set.add(d.actor);
    return Array.from(set).sort((a, b) => (a === 'user' ? -1 : b === 'user' ? 1 : a.localeCompare(b)));
  }

  // ---- persistence (section 4.6)

  toJSON(): WorkspaceSnapshot {
    return {
      version: 1,
      gameId: this.gameId,
      branch: this.branch,
      drafts: this.all,
      undo: this.undoStack,
      redo: this.redoStack,
      tempCounter: this.tempCounter,
      head: this.head,
    };
  }

  static fromJSON(snapshot: WorkspaceSnapshot): Workspace {
    const w = new Workspace(snapshot.gameId, snapshot.branch);
    for (const d of snapshot.drafts) w.drafts.set(d.key, d);
    w.undoStack = snapshot.undo ?? [];
    w.redoStack = snapshot.redo ?? [];
    w.tempCounter = snapshot.tempCounter ?? 0;
    w.head = snapshot.head ?? null;
    w.restoredCount = w.changeCount;
    return w;
  }

  static storageKey(gameId: string, branch: string): string {
    return `hue.workspace:${gameId}:${branch}`;
  }

  // ---- internals

  private apply(d: Draft, path: string, value: unknown): void {
    const now = Date.now();
    const existing = d.changes.find((c) => c.path === path);
    if (existing) {
      if (deepEqual(existing.before, value)) {
        d.changes.splice(d.changes.indexOf(existing), 1); // back to the server's value: no change left
      } else {
        existing.after = clone(value);
        existing.at = now;
      }
    } else {
      d.changes.push({ path, before: clone(getAt(d.current, path)), after: clone(value), at: now });
    }
    // a whole-value edit supersedes the leaf edits under it (a list reordered or an item removed, an object cleared)
    d.changes = d.changes.filter((c) => c.path === path || !(c.path.startsWith(path + '.') || c.path.startsWith(path + '[')));
    d.current = setAt(d.current, path, value === undefined ? undefined : clone(value));
    delete d.error;
    if (d.changes.length === 0 && d.status === 'modified') this.drafts.delete(d.key);
  }

  private describe(cmd: Command): string {
    const snapshotLabel = cmd.type === 'edit' ? cmd.label : cmd.type === 'restore' ? cmd.previous.label : cmd.snapshot.label;
    const label = this.drafts.get(cmd.key)?.label ?? snapshotLabel ?? splitKey(cmd.key)[1];
    switch (cmd.type) {
      case 'edit': {
        const field = cmd.path === '' || cmd.path === '$' ? 'value' : cmd.path.startsWith('data.') ? cmd.path.slice(5) : cmd.path;
        return `${field} of ${label}`;
      }
      case 'create':
        return `create of ${label}`;
      case 'remove':
        return `delete of ${label}`;
      case 'restore':
        return `restore of ${label}`;
    }
  }

  private push(cmd: Command): void {
    this.undoStack.push(cmd);
    this.redoStack = [];
  }

  private invert(cmd: Command, direction: 'undo' | 'redo'): void {
    switch (cmd.type) {
      case 'edit': {
        const d = this.drafts.get(cmd.key);
        const value = direction === 'undo' ? cmd.before : cmd.after;
        if (d) {
          if (d.status === 'deleted') return;
          this.apply(d, cmd.path, value);
          if (direction === 'undo' && !this.drafts.has(cmd.key)) {
            // the undo returned the entity to the server's value and dropped the draft: keep its base for a redo
            cmd.base = d.base;
            cmd.label = d.label;
            cmd.template = d.template;
          }
        } else if (direction === 'redo' && cmd.base) {
          const [kind, id] = splitKey(cmd.key);
          const again = this.ensure(kind, id, cmd.base, { label: cmd.label, template: cmd.template, actor: cmd.actor });
          this.apply(again, cmd.path, value);
        }
        return;
      }
      case 'create': {
        if (direction === 'undo') this.drafts.delete(cmd.key);
        else this.drafts.set(cmd.key, clone(cmd.snapshot));
        return;
      }
      case 'remove': {
        if (direction === 'undo') {
          if (cmd.previous) this.drafts.set(cmd.key, clone(cmd.previous));
          else this.drafts.delete(cmd.key);
        } else {
          if (cmd.snapshot.status === 'new') this.drafts.delete(cmd.key);
          else this.drafts.set(cmd.key, clone(cmd.snapshot));
        }
        return;
      }
      case 'restore': {
        if (direction === 'undo') this.drafts.set(cmd.key, clone(cmd.previous));
        else if (cmd.after) this.drafts.set(cmd.key, clone(cmd.after));
        else this.drafts.delete(cmd.key);
        return;
      }
    }
  }

  private forgetCommands(key: EntityKey): void {
    this.undoStack = this.undoStack.filter((c) => c.key !== key);
    this.redoStack = this.redoStack.filter((c) => c.key !== key);
  }

  private rewriteTempId(tempId: string, realId: string): void {
    const rewrite = (value: Json): Json => {
      if (value === tempId) return realId;
      if (Array.isArray(value)) return value.map(rewrite);
      if (value !== null && typeof value === 'object') {
        const out: Record<string, Json> = {};
        for (const [k, v] of Object.entries(value)) out[k] = rewrite(v);
        return out;
      }
      return value;
    };
    for (const d of this.drafts.values()) {
      d.current = rewrite(d.current);
      for (const c of d.changes) c.after = rewrite(c.after as Json);
    }
  }
}
